import { gql } from "lib/apolloClient";
import { pypElementRatingFragment } from "modules/CommonFragments";
import { categoryWiseAssessmentsFragment } from "./ClassProgressSummaryFragments";
import { iBPYPElementRatingFragment } from "CommonGraphql/IBPYPElementRating/IBPYPElementRatingFragments";

export const getCourseUnitPlansBasicDetailsFeedQuery = gql`
  query getCourseUnitPlansBasicDetailsFeed($id: ID!) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        flatUnitPlans {
          edges {
            node {
              id
              title {
                id
                value
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentsPypElementRatingsQuery = gql`
  query getStudentPypEvaluation(
    $studentIds: [ID!]
    $startDate: String
    $endDate: String
    $filtersForRecentMost: ibPYPElementRatingFilterForRecentMost
  ) {
    multiNode(ids: $studentIds, type: STUDENT) {
      id
      ... on Student {
        id
        ibPYPElementRatings(
          filtersForRecentMost: $filtersForRecentMost
          dataSet: MOST_RECENT
          startDate: $startDate
          endDate: $endDate
        ) {
          ...ratingItem
        }
      }
    }
  }
  ${pypElementRatingFragment.studentElementRatingSlim}
`;

export const getAllStudentsPypElementRatingsQuery = gql`
  query getAllStudentPypEvaluation(
    $studentIds: [ID!]
    $startDate: String
    $endDate: String
    $filtersForRecentMost: ibPYPElementRatingFilterForRecentMost
  ) {
    multiNode(ids: $studentIds, type: STUDENT) {
      id
      ... on Student {
        id
        ibPYPElementRatings(
          filtersForRecentMost: $filtersForRecentMost
          dataSet: MOST_RECENT
          startDate: $startDate
          endDate: $endDate
        ) {
          id
        }
      }
    }
  }
`;

export const getStudentsPypElementRatingHistoryQuery = gql`
  query getStudentPypEvaluation(
    $studentId: ID!
    $filtersForRatingHistory: ibPYPElementRatingFiltersForRatingHistory
    $endDate: String
    $curriculumProgramId: ID
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        id
        ibPYPElementRatings(
          filtersForRatingHistory: $filtersForRatingHistory
          dataSet: RATING_HISTORY
          endDate: $endDate
          curriculumProgramId: $curriculumProgramId
        ) {
          ...ratingItem
        }
      }
    }
  }
  ${iBPYPElementRatingFragment.studentElementRatingHistory}
`;

export const getClassProgressSummaryExportQuery = gql`
  query getClassProgressSummaryExport(
    $courseId: ID!
    $input: [ClassProgressSummaryExportInput!]
  ) {
    node(id: $courseId, type: COURSE) {
      id
      ... on Course {
        classProgressSummaryExport(input: $input)
      }
    }
  }
`;

export const getTermWiseProgressReportTaskGroupsQuery = gql`
  query getTermWiseProgressReportTaskGroupsQuery(
    $academicYearId: ID!
    $termFilters: TermFilters
  ) {
    node(id: $academicYearId, type: ACADEMIC_YEAR) {
      id
      ... on AcademicYear {
        id
        terms(filters: $termFilters) {
          id
          label
          dependentEntities(
            filters: { entityTypes: [PROGRESS_REPORT_TASK_GROUP] }
          ) {
            id
            ... on ProgressReportTaskGroup {
              id
              title
            }
            __typename
          }
        }
      }
    }
  }
`;

export const getCategoryWiseAssessmentsQuery = gql`
  query getCategoryWiseAssessments(
    $id: ID!
    $grades: [ID!]
    $academicCriteriaSetsFilter: academicCriteriaSetsFilter
    $assignmentFilters: AssignmentFeedFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        constants {
          academicCriteriaSets(
            grades: $grades
            filters: $academicCriteriaSetsFilter
          ) {
            id
            label
            isShowGradeBoundaries
            academicCriteriaValues {
              id
              label
              color
              scoreValues {
                id
                value
                genericTag {
                  id
                  label
                }
                category {
                  id
                }
              }
              gradeBoundaries {
                id
              }
              ...categoryWiseAssessmentItem
            }
          }
        }
      }
    }
  }
  ${categoryWiseAssessmentsFragment.assignmentsFragment}
`;

export const getStudentWiseRatingsQuery = gql`
  query getStudentWiseRatings(
    $id: ID!
    $filtersForGeneric: ibPYPElementRatingFiltersForGeneric
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        students {
          edges {
            node {
              id
              ibPYPElementRatings(
                dataSet: ASSESSMENT_CATEGORY_SCORE
                filtersForGeneric: $filtersForGeneric
              ) {
                id
                academicCriteriaValue {
                  id
                  label
                }
                academicCriteriaSet {
                  id
                  criteriaType
                  subType
                  parentEntities {
                    id
                    ... on AcademicCriteriaValue {
                      id
                    }
                  }
                }
                ibPYPElementType
                isComputedRating
                parent {
                  id
                  ... on StudentAssignment {
                    id
                  }
                }
                academicTerm {
                  id
                }
                value
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentsTermWiseProgressReportsRatingsQuery = gql`
  query getStudentsTermWiseProgressReportsRatings(
    $studentIds: [ID!]
    $filtersForRatingHistory: ibPYPElementRatingFiltersForRatingHistory
  ) {
    multiNode(ids: $studentIds, type: STUDENT) {
      id
      ... on Student {
        id
        firstName
        lastName
        profileImage
        ibPYPElementRatings(
          filtersForRatingHistory: $filtersForRatingHistory
          dataSet: RATING_HISTORY
        ) {
          id
          ibPYPElementId
          ibPYPElementType
          value
          academicCriteriaSet {
            id
            label
            criteriaType
          }
          academicCriteriaValue {
            id
            label
            abbreviation
          }
          parentType
          parent {
            ... on StudentProgressReport {
              id
              courseProgressReportTask {
                id
                progressReportTaskGroup {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getPlannerElementNodesOfSetMinifiedQuery = gql`
  query getPlannerElementNodesOfSet($id: ID!, $filters: PlannerElementFilters) {
    node(id: $id, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        id
        nodes(filters: $filters) {
          id
        }
      }
    }
  }
`;
